/**
 * @fileoverview Notification preferences matrix.
 * @description The channel matrix of the profile's Notifications tab: a header with one column per
 * channel, then a "Categories" block and a "Notification types" block, each row labelled with the
 * translated type/category (a readable fallback for codes the backend adds later) and one checkbox
 * per channel. Mandatory or admin-fixed cells render checked/unchecked but disabled; a missing cell
 * shows a dash. Purely presentational — drafts and saving live in useNotificationPreferencesForm.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  currentValue,
  humanizeCode,
  isPreferenceLocked,
  preferenceTestId,
} from '@/features/notifications/utils/notification-preferences.utils'
import Badge from '@/shared/components/atoms/Badge'
import Checkbox from '@/shared/components/atoms/Checkbox'

import type { NotificationPreference } from '@/features/notifications/services/notification-preferences.service'
import type {
  PreferenceDrafts,
  PreferenceMatrix,
  PreferenceMatrixRow,
} from '@/features/notifications/utils/notification-preferences.utils'

interface NotificationPreferencesTableProps {
  matrix: PreferenceMatrix
  drafts: PreferenceDrafts
  disabled: boolean
  onToggle: (preference: NotificationPreference, next: boolean) => void
}

interface PreferenceRowProps {
  row: PreferenceMatrixRow
  scope: 'categories' | 'types'
  channels: string[]
  drafts: PreferenceDrafts
  disabled: boolean
  onToggle: (preference: NotificationPreference, next: boolean) => void
}

interface PreferenceCellProps {
  preference: NotificationPreference | undefined
  label: string
  testId: string
  drafts: PreferenceDrafts
  disabled: boolean
  onToggle: (preference: NotificationPreference, next: boolean) => void
}

/**
 * @component
 * @param {PreferenceCellProps} props - One channel cell of a row.
 * @param {NotificationPreference | undefined} props.preference - The cell's row, if the backend sent one.
 * @param {string} props.label - Accessible name, e.g. "Prospect assigned — Email".
 * @param {string} props.testId - QA locator of the checkbox.
 * @param {PreferenceDrafts} props.drafts - The unsaved toggles.
 * @param {boolean} props.disabled - Whether every toggle is disabled (a save is in flight).
 * @param {(preference: NotificationPreference, next: boolean) => void} props.onToggle - Toggle handler.
 */
function PreferenceCell({
  preference,
  label,
  testId,
  drafts,
  disabled,
  onToggle,
}: PreferenceCellProps) {
  if (!preference) {
    return <td className="py-2.5 text-center text-slate">—</td>
  }

  /**
   * @description Reports the checkbox's new state.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event.
   */
  const change = (event: React.ChangeEvent<HTMLInputElement>) =>
    onToggle(preference, event.target.checked)

  return (
    <td className="py-2.5 text-center">
      <span className="inline-flex">
        <Checkbox
          aria-label={label}
          data-testid={testId}
          checked={currentValue(preference, drafts)}
          disabled={disabled || isPreferenceLocked(preference)}
          onChange={change}
        />
      </span>
    </td>
  )
}

/**
 * @component
 * @param {PreferenceRowProps} props - One type/category row and its shared state.
 * @param {PreferenceMatrixRow} props.row - The row.
 * @param {'categories' | 'types'} props.scope - Which translation group labels it.
 * @param {string[]} props.channels - The channel columns, in order.
 * @param {PreferenceDrafts} props.drafts - The unsaved toggles.
 * @param {boolean} props.disabled - Whether every toggle is disabled.
 * @param {(preference: NotificationPreference, next: boolean) => void} props.onToggle - Toggle handler.
 */
function PreferenceRow({ row, scope, channels, drafts, disabled, onToggle }: PreferenceRowProps) {
  const { t } = useTranslation()
  const name = t(`profile.notifications.${scope}.${row.key}`, {
    defaultValue: humanizeCode(row.key),
  })
  const isRequired = Object.values(row.cells).some((cell) => cell.isMandatory === true)

  return (
    <tr className="border-b border-divider last:border-b-0" data-testid={preferenceTestId(row.id)}>
      <th scope="row" className="py-2.5 pr-3 text-left text-sm font-medium text-ink">
        <span className="flex items-center gap-2">
          {name}
          {isRequired && (
            <Badge variant="neutral" data-testid={`${preferenceTestId(row.id)}-required`}>
              {t('profile.notifications.required')}
            </Badge>
          )}
        </span>
      </th>
      {channels.map((channel) => (
        <PreferenceCell
          key={channel}
          preference={row.cells[channel]}
          testId={preferenceTestId(row.id, channel)}
          label={t('profile.notifications.toggleLabel', {
            name,
            channel: t(`profile.notifications.channels.${channel}`, {
              defaultValue: humanizeCode(channel),
            }),
          })}
          drafts={drafts}
          disabled={disabled}
          onToggle={onToggle}
        />
      ))}
    </tr>
  )
}

/**
 * @component
 * @param {NotificationPreferencesTableProps} props - The matrix and its state.
 * @param {PreferenceMatrix} props.matrix - Channels, category rows and type rows.
 * @param {PreferenceDrafts} props.drafts - The unsaved toggles.
 * @param {boolean} props.disabled - Whether every toggle is disabled (a save is in flight).
 * @param {(preference: NotificationPreference, next: boolean) => void} props.onToggle - Toggle handler.
 */
function NotificationPreferencesTable({
  matrix,
  drafts,
  disabled,
  onToggle,
}: NotificationPreferencesTableProps) {
  const { t } = useTranslation()
  const blocks = [
    {
      scope: 'categories',
      rows: matrix.categories,
      title: t('profile.notifications.categoriesTitle'),
    },
    { scope: 'types', rows: matrix.types, title: t('profile.notifications.typesTitle') },
  ] as const

  return (
    <table
      className="w-full border-collapse"
      aria-label={t('profile.notifications.title')}
      data-testid="notification-preferences-table"
    >
      <thead>
        <tr className="border-b border-divider">
          <th scope="col" className="py-2 text-left text-xs font-semibold text-slate">
            <span className="sr-only">{t('profile.notifications.nameColumn')}</span>
          </th>
          {matrix.channels.map((channel) => (
            <th
              key={channel}
              scope="col"
              className="py-2 text-center text-xs font-semibold text-slate"
            >
              {t(`profile.notifications.channels.${channel}`, {
                defaultValue: humanizeCode(channel),
              })}
            </th>
          ))}
        </tr>
      </thead>
      {blocks
        .filter((block) => block.rows.length > 0)
        .map((block) => (
          <tbody key={block.scope} data-testid={`notification-preferences-${block.scope}`}>
            <tr>
              <th
                scope="colgroup"
                colSpan={matrix.channels.length + 1}
                className="pt-4 pb-1 text-left text-caption font-semibold tracking-wide text-slate uppercase"
              >
                {block.title}
              </th>
            </tr>
            {block.rows.map((row) => (
              <PreferenceRow
                key={row.id}
                row={row}
                scope={block.scope}
                channels={matrix.channels}
                drafts={drafts}
                disabled={disabled}
                onToggle={onToggle}
              />
            ))}
          </tbody>
        ))}
    </table>
  )
}

export default NotificationPreferencesTable
